import { useContext, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Typography from "@tiptap/extension-typography";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import Focus from "@tiptap/extension-focus";
import Link from "@tiptap/extension-link";
import Document from "@tiptap/extension-document";
import cx from "classnames";

import type { EditorProps } from "./types";
import { editorContext } from "./context";
import { Toolbar } from "./Toolbar";
import { useSecondaryFloatingPanelsStore } from "./Toolbar/context";
import { ContextualMenu } from "./ContextualMenu";
import { Heading } from "./Heading";
import { Paragraph } from "./Paragraph/Paragraph";
import { HorizontalRule } from "./HorizontalRule/HorizontalRule";
import { BlockId } from "./BlockId";
import { Footnote } from "./Footnote";
import { CustomImage, Figure } from "./Figure";
import { LinkUrlTooltip } from "./CustomLink";
import { CodeBlockExtension } from "./CodeBlockExtension";
import { Preview } from "./Embed";
import { getWordcountWithoutTitle } from "./utils";
import { LinkPanelContext } from "./LinkPanel/context";
import styles from "./Editor.module.css";
import "./EditorCodeHighlightTheme.css";

const CustomDocument = Document.extend({
  content: "heading block*",
});

export const Editor = ({
  text: content,
  onChange,
  noIndent,
  placeholder,
  className,
  onCmdSave,
}: EditorProps) => {
  const { hasFocus } = useContext(editorContext);
  const { t } = useTranslation("tale", { useSuspense: true });
  const editor = useEditor({
    immediatelyRender: false,
    autofocus: true,
    editorProps: {
      attributes: {
        class: "relative",
      },
    },
    extensions: [
      CustomDocument,
      StarterKit.configure({
        document: false, // Swapped for CustomDocument above.
        heading: false, // Swapped for custom Heading below.
        codeBlock: false,
        paragraph: false,
        horizontalRule: false,
      }),
      Typography,
      Underline,
      Placeholder.configure({
        showOnlyCurrent: false,
        placeholder: ({ editor, node }) => {
          if (node.type.name === "heading" && node.attrs?.level === 1) {
            return "Your Title";
          }

          if (editor.isEmpty) return placeholder || t("editorPlaceholder");

          return t("editorPlaceholderSecondary");
        },
      }),
      CharacterCount,
      Focus,
      Link,
      // Custom extensions below:
      ContextualMenu,
      CustomImage,
      Footnote,
      Figure,
      Heading.configure({
        levels: [1, 2, 3, 4],
      }),
      Paragraph,
      HorizontalRule,
      BlockId,
      CodeBlockExtension,
      Preview,
    ],
    content,
    onUpdate: ({ editor }) => {
      const text = editor.getText();
      const [title] = text.split("\n");

      onChange({
        title,
        text: editor.getHTML(),
        text_json: editor.getJSON(),
        wordCount: getWordcountWithoutTitle(editor),
      });
    },
  });
  const { isSecondaryPanelDisplayed, setIsSecondaryPanelDisplayed } =
    useSecondaryFloatingPanelsStore();

  useEffect(() => {
    if (editor !== null && !editor.isDestroyed) {
      editor.commands.focus();
    }
  }, [editor, hasFocus]);

  useEffect(() => {
    if (!!onCmdSave) {
      const handleCmdSaveKeyDown = (event: KeyboardEvent) => {
        if (event.key === "s" && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          onCmdSave();
        }
      };

      document.addEventListener("keydown", handleCmdSaveKeyDown);

      return () => {
        document.removeEventListener("keydown", handleCmdSaveKeyDown);
      };
    }
  }, [onCmdSave]);

  const wordCountWithoutTitle = useMemo(
    () => getWordcountWithoutTitle(editor),
    [editor]
  );

  return (
    <div className="relative w-full h-auto bg-white">
      <div
        className={cx(
          styles.EditorWorkingArea,
          {
            [styles.EditorWorkingAreaHasFocus]: hasFocus,
            [styles.EditorWorkingAreaNoIndent]: noIndent,
          },
          className
        )}
      >
        <EditorContent
          data-editor
          editor={editor}
          onKeyDown={(e) => {
            if (editor !== null && e.code === "Escape") {
              const { from } = editor.state.selection;

              editor
                ?.chain()
                .setTextSelection(from || 0)
                .run();
            }
          }}
          className={styles.EditorContent}
        />
      </div>
      {editor !== null && (
        <div
          className={cx(
            "fixed bottom-4 left-64 py-1.5 px-2.5 bg-black rounded transition-opacity z-20",
            {
              "opacity-0 pointer-events-none": hasFocus,
            }
          )}
        >
          <span className="text-white font-medium text-sm">
            {t("wordCount", { count: wordCountWithoutTitle })}
          </span>
        </div>
      )}
      {editor !== null && (
        <LinkPanelContext>
          {({ setIsPoppableVisible }) => (
            <>
              {isSecondaryPanelDisplayed &&
                createPortal(
                  <div
                    className="fixed top-0 left-0 z-10 w-full h-full"
                    onClick={() => {
                      setIsPoppableVisible!(false);
                      setIsSecondaryPanelDisplayed(false);
                      editor.chain().focus().run();
                    }}
                  />,
                  document.body
                )}
              <Toolbar editor={editor} />
              <LinkUrlTooltip editor={editor} />
            </>
          )}
        </LinkPanelContext>
      )}
    </div>
  );
};
